<template>
	<common-card title="Advanced Settings" type="minimize" default-status="min">
		<template #title>
			<span class="advanced-settings-card-title"
				>Advanced Settings
				<span
					class="advanced-settings-enabled-flag"
					:class="{ 'advanced-settings-enabled': hasAdvancedSettings }"
					>Enabled</span
				></span
			>
		</template>

		<campaigns-tooltip-label
			v-if="posts.length"
			tooltip="Never loads the campaign on the selected posts and/or pages. Does not disable automatic global output."
			label="Never load campaign on selected posts and/or pages:"
		>
			<core-form-multi-select-input
				name="settingNever"
				:value="settingNever"
				:options="formattedPosts"
				class="omapi-input"
				search-placeholder="Type to search for posts and pages"
				@input="setMultiSelectValue"
			/>
		</campaigns-tooltip-label>

		<campaigns-tooltip-label
			v-if="posts.length"
			tooltip="Loads the campaign on the selected posts and/or pages."
			label="Load campaign specifically on selected posts and/or pages:"
		>
			<core-form-multi-select-input
				name="settingOnly"
				:value="settingOnly"
				:options="formattedPosts"
				class="omapi-input"
				search-placeholder="Type to search for posts and pages"
				@input="setMultiSelectValue"
			/>
		</campaigns-tooltip-label>

		<core-loading v-if="isLoading"><h1>Loading...</h1></core-loading>

		<campaigns-taxonomies-settings v-if="!isLoading" :campaign="campaign" :taxonomies="regularTaxonomies" />

		<campaigns-tooltip-label
			tooltip="Loads the campaign on the selected post types and archive pages."
			label="Load on Post Types and Archives:"
		>
			<common-selectable-group
				class="text-setting omapi-input"
				type="checkbox"
				group-name="settingShow"
				:options="showOptions"
				:value="settingShow"
				@input="setShowValue"
			/>
		</campaigns-tooltip-label>

		<slot />
	</common-card>
</template>

<script lang="js">
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
import { mapState, mapGetters } from 'vuex'
import { campaign } from '@/campaigns/mixins/campaign'

export default  {
	mixins : [ campaign ],
	props  : {
		campaign : {
			type     : Object,
			required : true,
		},
	},
	computed : {
		...mapGetters([ 'isFetched' ]),
		...mapState('wp', [ 'posts', 'postTypes', 'taxonomies' ]),
		isLoading () {
			return !this.isFetched('wpResources')
		},
		showOptions () {
			let options = []

			// Get types, sorted by "page" capability_type.
			const types = cloneDeep(this.postTypes)
				.sort(o => 'page' === get(o, 'capability_type') ? -1 : 1)

			Object.values(types).forEach(option => {
				option.value = option.name
				option.titleTag = 'Post Type: ' + option.name

				options.push(option)

				// If the post type has "page" capability_type, we probably
				// shouldn't show the {single posttype} option, since that's
				// likely the only view they will see, and it's confusing.
				if ('page' !== get(option, 'capability_type')) {
					// Add singular option.
					options.push({
						value    : 'singular___' + option.value,
						label    : 'Single ' + option.label,
						titleTag : option.titleTag,
						indented : true,
					})
				}
			})

			options = [
				{
					value    : 'index',
					label    : 'Front Page and Search Pages',
					titleTag : 'index',
				},
				...options,
			]

			return options
		},
		regularTaxonomies () {
			const taxes = this.filterTaxonomies((tax) => {
				return 'category' !== tax.name && !this.isPluginTaxonomy(tax) && 0 < tax.terms.length
			})
			if (this.$get('taxonomies.category')) {
				const cats = this.taxonomies.category

				taxes.unshift({
					name    : cats.name,
					label   : 'Category Archives and Posts within the Category',
					tooltip : 'Category Archives and Categories',
					terms   : cats.terms,
					for     : cats.for,
				})
			}
			return taxes
		},
		formattedPosts () {
			const transform = post => {
				const value = post.value ? post.value.toString() : ''
				return {
					...post,
					name       : post.name + (value ? ` (${value})` : ''),
					fancyLabel : post.name + (value ? ` <em>(${value})</em>` : ''),
				}
			}

			return this.posts.map(transform)
		},
		/**
		 * Whether any of the advanced settings have been set.
		 *
		 * @since  2.0.0
		 * @since 2.8.0 Moved from mixins/campaign.js
		 *
		 * @returns {boolean|string} If campaign has advanced settings set.
		 */
		hasAdvancedSettings () {
			if (this.settingNever && this.settingNever.length) {
				return 'settingNever'
			}

			if (this.settingOnly && this.settingOnly.length) {
				return 'settingOnly'
			}

			if (this.settingShow && this.settingShow.length) {
				return 'settingShow'
			}

			return Object.entries(this.$get('$store.state.wp.taxonomies', {})).some(([ key, taxonomy ]) => {
				if (this.isPluginTaxonomy(taxonomy)) {
					return false
				}

				key = 'category' === key ? 'categories' : key

				const value = this.$get(
					`campaign.wp.post_meta._omapi_${key}`,
					this.$get(`campaign.wp.post_meta._omapi_taxonomies.${key}`, []),
				)
				return value && value.length
			})
		},
	},
	methods : {
		/**
		 * Set the value of our settingShow property using passed event.
		 *
		 * @since 2.6.6
		 *
		 * @param {Object} event Event object
		 *
		 * @returns {void}
		 */
		setShowValue (event) {
			this.setValue(event)

			const { value, name, checked } = event.target
			const child = this.showOptions.find(o => o.value === 'singular___' + value)

			// If no child found, do not proceed.
			if (!child) {
				return
			}

			const childChecked = this.settingShow.includes(child.value)
			const previouslyMatched = (!checked && childChecked) || (checked && !childChecked)

			// If the child/parent options didn't previously match, don't bother keeping in sync.
			if (!previouslyMatched) {
				return
			}

			// Sync the child checked value with the parent.
			this.setValue({
				target : {
					name,
					value : child.value,
				},
			})
		},
	},
};
</script>
